import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from '../App';
import { useLocation } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  Mic, Square, Loader2, Send, Paperclip, X, Sparkles,
  Volume2, Trash2, FileText, Image as ImageIcon, FileSpreadsheet,
  MessageCircle, BookOpen, CheckCircle2, AlertCircle, Languages, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * r66.6 — "Professor Owl" mascot v2: realistic proportions with subtle
 * animations (slow bob, blink every ~5s, occasional head tilt). Big-by-default
 * but fully size-controllable via className. Uses CSS keyframes injected once
 * so multiple instances don't fight each other.
 */
const OWL_STYLES = `
  @keyframes mm-buddha-float {
    0%, 100% { transform: translateY(0) rotate(-1deg); }
    50% { transform: translateY(-6%) rotate(1deg); }
  }
  @keyframes mm-buddha-glow {
    0%, 100% { opacity: 0.5; transform: scale(1); }
    50% { opacity: 0.85; transform: scale(1.18); }
  }
  .mm-owl-svg { animation: mm-buddha-float 4.6s ease-in-out infinite; transform-origin: 50% 90%; }
  .mm-buddha-glow { animation: mm-buddha-glow 3.4s ease-in-out infinite; transform-origin: center; }
`;

/** Master Mind mascot — uses the user-provided Buddha PNG. */
const NerdFace = ({
  className = 'w-10 h-10',
  animated = true,
}) => (
  <>
    <style>{OWL_STYLES}</style>
    <span className={`relative inline-block ${className}`} aria-hidden>
      {/* Soft amber halo behind the mascot */}
      <span
        className={`absolute inset-0 rounded-full bg-amber-300/50 blur-xl ${animated ? 'mm-buddha-glow' : ''}`}
      />
      <img
        src="/mascots/master-mind-buddha.png"
        alt="Master Mind"
        draggable={false}
        className={`relative w-full h-full object-contain select-none pointer-events-none ${animated ? 'mm-owl-svg' : ''}`}
        style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.25))' }}
      />
    </span>
  </>
);

const HIDE_ON_PATHS = [
  /^\/login/i, /^\/signup/i, /^\/register/i, /^\/public\//i,
  /^\/student\//i, /^\/forgot/i, /^\/reset/i, /^\/$/,
];

const fileIcon = (mime, name) => {
  const n = (name || '').toLowerCase();
  if ((mime || '').startsWith('image/')) return <ImageIcon className="w-3.5 h-3.5" />;
  if (n.endsWith('.pdf') || (mime || '').includes('pdf')) return <FileText className="w-3.5 h-3.5" />;
  if (n.endsWith('.xlsx') || n.endsWith('.xls') || n.endsWith('.csv'))
    return <FileSpreadsheet className="w-3.5 h-3.5" />;
  return <FileText className="w-3.5 h-3.5" />;
};

/** Pulsing soundwave used while recording / synthesising — pure CSS. */
const Soundwave = ({ active, color = 'bg-amber-500' }) => (
  <div className="flex items-end gap-0.5 h-6" aria-hidden>
    {[0, 1, 2, 3, 4].map((i) => (
      <span
        key={i}
        className={`w-1 rounded-full ${color} ${active ? 'animate-wave' : 'opacity-40'}`}
        style={{
          height: active ? `${20 + (i % 3) * 8}%` : '20%',
          animationDelay: `${i * 0.1}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes wave {
        0%, 100% { height: 18%; }
        50% { height: 100%; }
      }
      .animate-wave { animation: wave 0.9s ease-in-out infinite; }
    `}</style>
  </div>
);

const KnowledgeBubble = () => {
  const { api, user } = useAuth();
  const isAuthenticated = !!user;
  const isBPOAgent = (user?.role || '').toLowerCase() === 'bpo_agent';
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('ask');

  // ASK ME state
  const [askQuestion, setAskQuestion] = useState('');
  const [askLoading, setAskLoading] = useState(false);
  const [askResult, setAskResult] = useState(null); // { answer, citations[] }
  const [askRecording, setAskRecording] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const askAudioRef = useRef(null);

  // TELL ME state
  const [tellText, setTellText] = useState('');
  const [tellFiles, setTellFiles] = useState([]);
  const [tellRecording, setTellRecording] = useState(false);
  const [tellRecordedBlob, setTellRecordedBlob] = useState(null);
  const [tellSubmitting, setTellSubmitting] = useState(false);
  const [recentEntries, setRecentEntries] = useState([]);
  const [recentSearch, setRecentSearch] = useState('');
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editDraftText, setEditDraftText] = useState('');
  const [editDraftTitle, setEditDraftTitle] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const mediaRecRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const askMediaRecRef = useRef(null);
  const askMediaChunksRef = useRef([]);

  const hidden = useMemo(
    () => !isAuthenticated || isBPOAgent || HIDE_ON_PATHS.some((re) => re.test(location.pathname)),
    [isAuthenticated, isBPOAgent, location.pathname]
  );

  // Fetch all entries for the TELL ME tab feed.
  // No limit — the team's full knowledge timeline is loaded so older notes are
  // never hidden. Backend already excludes the heavy `tokens` field, so the
  // payload stays small.
  const fetchRecent = useCallback(async () => {
    try {
      const params = recentSearch.trim() ? `?q=${encodeURIComponent(recentSearch.trim())}` : '';
      const r = await api().get(`/knowledge/entries${params}`);
      setRecentEntries(r.data?.entries || []);
    } catch (_e) { setRecentEntries([]); }
  }, [api, recentSearch]);

  useEffect(() => {
    if (open && tab === 'tell') fetchRecent();
  }, [open, tab, fetchRecent]);

  const reset = () => {
    setAskQuestion('');
    setAskResult(null);
    setTellText('');
    setTellFiles([]);
    setTellRecordedBlob(null);
    if (askAudioRef.current) { askAudioRef.current.pause(); askAudioRef.current = null; }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) setTimeout(reset, 200);
  };

  // ---------- Voice recording helpers ----------
  // Pick a mimeType the current browser can actually record.
  // Chromium/Firefox prefer `audio/webm`; Safari (incl. iOS) only supports
  // `audio/mp4`. Falling back to an empty string lets the browser choose its
  // default, which is better than throwing.
  const pickRecorderMime = () => {
    if (typeof window === 'undefined' || !window.MediaRecorder) return { mime: '', ext: 'webm' };
    const candidates = [
      { mime: 'audio/webm;codecs=opus', ext: 'webm' },
      { mime: 'audio/webm',             ext: 'webm' },
      { mime: 'audio/mp4',              ext: 'm4a' },   // Safari / iOS
      { mime: 'audio/ogg;codecs=opus',  ext: 'ogg' },
      { mime: 'audio/wav',              ext: 'wav' },
    ];
    for (const c of candidates) {
      if (window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(c.mime)) {
        return c;
      }
    }
    return { mime: '', ext: 'webm' };  // Last resort — browser default
  };

  const startRecording = async (which) => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !window.MediaRecorder) {
      toast.error('Recording is not supported by this browser. Try Chrome, Firefox or Safari 14+.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const { mime, ext } = pickRecorderMime();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
      rec.onstop = () => {
        // Use the recorder's actual mimeType (it may differ from `mime` if the
        // browser auto-selected one). This keeps the Blob's `type` honest so
        // the backend Whisper call can derive the correct extension.
        const finalType = rec.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunks, { type: finalType });
        blob.name = `note.${ext}`;  // helps the backend route to Whisper's format check
        stream.getTracks().forEach((t) => t.stop());
        if (which === 'ask') {
          handleAskVoiceFinish(blob, ext);
        } else {
          setTellRecordedBlob(blob);
        }
      };
      rec.start();
      if (which === 'ask') {
        askMediaRecRef.current = rec;
        askMediaChunksRef.current = chunks;
        setAskRecording(true);
      } else {
        mediaRecRef.current = rec;
        mediaChunksRef.current = chunks;
        setTellRecording(true);
      }
    } catch (e) {
      // Distinguish permission denial vs not-supported for clearer error toast
      const denied = e?.name === 'NotAllowedError' || /denied/i.test(e?.message || '');
      toast.error(denied
        ? 'Microphone permission was denied. Allow it in your browser settings and try again.'
        : 'Could not start the recording. Please try another browser.');
    }
  };

  const stopRecording = (which) => {
    if (which === 'ask') {
      askMediaRecRef.current?.stop();
      setAskRecording(false);
    } else {
      mediaRecRef.current?.stop();
      setTellRecording(false);
    }
  };

  // ASK voice — auto-transcribe then submit
  const handleAskVoiceFinish = async (blob, ext = 'webm') => {
    setAskLoading(true);
    try {
      const form = new FormData();
      form.append('audio', blob, `question.${ext}`);
      const r = await api().post('/knowledge/transcribe', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const eng = r.data?.english_text || r.data?.text || '';
      setAskQuestion(eng);
      if (eng.trim()) await runAsk(eng);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Voice transcription failed');
    } finally {
      setAskLoading(false);
    }
  };

  // ---------- ASK ----------
  const runAsk = async (qOverride) => {
    const q = (qOverride ?? askQuestion).trim();
    if (!q) return;
    setAskLoading(true);
    setAskResult(null);
    try {
      const r = await api().post('/knowledge/ask', { question: q });
      setAskResult(r.data);
      // r66.7 — DO NOT auto-play TTS. User must click the speaker button on
      // the answer card to hear it. This avoids burning OpenAI TTS credits
      // on every single ASK call (most users read, only some listen).
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ask failed');
    } finally {
      setAskLoading(false);
    }
  };

  const speakAnswer = async (text) => {
    if (askAudioRef.current) { askAudioRef.current.pause(); askAudioRef.current = null; }
    setTtsLoading(true);
    try {
      const r = await api().post('/knowledge/tts', { text }, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(r.data);
      const audio = new Audio(blobUrl);
      askAudioRef.current = audio;
      audio.play().catch(() => {});
    } catch (_e) {
      // TTS is best-effort; don't toast
    } finally {
      setTtsLoading(false);
    }
  };

  const stopSpeaking = () => {
    if (askAudioRef.current) { askAudioRef.current.pause(); askAudioRef.current = null; }
  };

  // ---------- TELL ----------
  const submitTell = async () => {
    if (!tellText.trim() && !tellRecordedBlob && tellFiles.length === 0) {
      toast.error('Add some text, record a voice note, or attach a file.');
      return;
    }
    setTellSubmitting(true);
    try {
      const form = new FormData();
      if (tellText.trim()) form.append('text', tellText.trim());
      if (tellRecordedBlob) {
        // Use the blob's .name when present (set by the recorder) so the
        // backend Whisper helper picks the right file extension.
        const fname = tellRecordedBlob.name || 'note.webm';
        form.append('audio', tellRecordedBlob, fname);
      }
      tellFiles.forEach((f) => form.append('files', f, f.name));
      const r = await api().post('/knowledge/entries', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Saved: ${r.data.title || 'New entry'}`);
      setTellText('');
      setTellFiles([]);
      setTellRecordedBlob(null);
      fetchRecent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setTellSubmitting(false);
    }
  };

  const removeFile = (idx) => setTellFiles(tellFiles.filter((_, i) => i !== idx));

  // ---------- Edit / Delete an existing entry ----------
  const userIsAdmin = ['admin', 'super_admin'].includes((user?.role || '').toLowerCase());
  const canModify = (entry) => (
    entry?.submitter_user_id === user?.id || userIsAdmin
  );

  const startEdit = (entry) => {
    setEditingEntryId(entry.id);
    setEditDraftTitle(entry.title || '');
    setEditDraftText(entry.original_text || entry.english_text || '');
  };

  const cancelEdit = () => {
    setEditingEntryId(null);
    setEditDraftText('');
    setEditDraftTitle('');
  };

  const saveEdit = async (entryId) => {
    if (!editDraftText.trim()) { toast.error('Entry text cannot be empty'); return; }
    setEditSaving(true);
    try {
      await api().put(`/knowledge/entries/${entryId}`, {
        text: editDraftText.trim(),
        title: editDraftTitle.trim() || undefined,
      });
      toast.success('Entry updated');
      cancelEdit();
      fetchRecent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Delete this knowledge entry? This cannot be undone.')) return;
    try {
      await api().delete(`/knowledge/entries/${entryId}`);
      toast.success('Entry deleted');
      fetchRecent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (hidden) return null;

  return (
    <>
      {/* Floating Buddha bubble — amber/yellow gradient (matches the modal). */}
      <div className="fixed bottom-[19rem] right-6 z-50 flex items-center gap-2">
        <span
          className="hidden sm:inline-flex select-none items-center gap-1 px-2.5 py-1 rounded-full
                     bg-white/95 backdrop-blur border border-amber-200 text-[11px] font-semibold
                     text-amber-700 shadow-sm whitespace-nowrap"
          aria-hidden
        >
          Master Mind
        </span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group relative w-16 h-16 rounded-full shadow-xl ring-1 ring-amber-200
                     bg-gradient-to-br from-amber-200 via-yellow-300 to-amber-400
                     hover:from-amber-100 hover:via-yellow-200 hover:to-amber-300
                     transition-transform duration-200 hover:scale-110 active:scale-95"
          data-testid="knowledge-bubble-fab"
          aria-label="MASTER MIND Knowledgebase"
          title="Master Mind"
          style={{ overflow: 'visible' }}
        >
          <span className="absolute inset-0 rounded-full bg-amber-300/40 animate-ping group-hover:animate-none" />
          {/* Buddha mascot floats up above the bubble — head pokes out the top */}
          <NerdFace className="absolute left-1/2 -translate-x-1/2 -top-5 w-[5.5rem] h-[5.5rem]" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="max-w-5xl w-[95vw] h-[85vh] max-h-[85vh] p-0 overflow-hidden bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 flex flex-col"
          data-testid="knowledge-bubble-modal"
        >
          {/* Header */}
          <div className="relative shrink-0 p-6 pb-4 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-br from-amber-50 via-orange-50 to-white dark:from-amber-950/40 dark:via-orange-950/30 dark:to-slate-950 overflow-hidden">
            <div className="absolute -top-12 -right-12 w-44 h-44 bg-amber-200/40 dark:bg-amber-700/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-12 -left-12 w-44 h-44 bg-orange-200/40 dark:bg-orange-700/20 rounded-full blur-3xl" />
            <DialogHeader className="relative">
              <DialogTitle className="flex items-center gap-3 text-slate-900 dark:text-white text-xl">
                <span className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 shadow-md shadow-orange-500/20 text-white">
                  <BookOpen className="w-5 h-5" />
                </span>
                MASTER MIND
                <Sparkles className="w-4 h-4 text-amber-500 ml-auto" />
              </DialogTitle>
              <DialogDescription className="text-slate-500 dark:text-slate-400 text-sm">
                The team's collective knowledge — contribute via <strong>TELL ME</strong>, query via <strong>ASK ME</strong>. Speak any language; answers come back in English.
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Tabs panel — fills remaining height; inner content scrolls */}
          <div className="flex-1 min-h-0 p-6 bg-white dark:bg-slate-950 overflow-y-auto">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="w-full grid grid-cols-2 mb-4" data-testid="knowledge-tabs">
                <TabsTrigger value="ask" className="data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700" data-testid="tab-ask">
                  <MessageCircle className="w-4 h-4 mr-1.5" />
                  ASK ME
                </TabsTrigger>
                <TabsTrigger value="tell" className="data-[state=active]:bg-orange-100 data-[state=active]:text-orange-700" data-testid="tab-tell">
                  <BookOpen className="w-4 h-4 mr-1.5" />
                  TELL ME
                </TabsTrigger>
              </TabsList>

              {/* ===== ASK ME ===== */}
              <TabsContent value="ask" className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={askQuestion}
                    onChange={(e) => setAskQuestion(e.target.value)}
                    placeholder="Ask anything…  e.g.,  What's our commission rate at Sunderland?"
                    className="text-base py-6 focus-visible:ring-amber-500/40"
                    onKeyDown={(e) => e.key === 'Enter' && !askLoading && runAsk()}
                    data-testid="knowledge-ask-input"
                  />
                  <Button
                    onClick={() => runAsk()}
                    disabled={askLoading || !askQuestion.trim()}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="knowledge-ask-send"
                  >
                    {askLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => askRecording ? stopRecording('ask') : startRecording('ask')}
                    className={askRecording ? 'border-orange-500 text-orange-600' : ''}
                    data-testid="knowledge-ask-mic"
                  >
                    {askRecording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                </div>

                {askRecording && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
                    <Soundwave active color="bg-orange-500" />
                    <span className="text-sm font-medium text-orange-700">Listening… speak your question, tap stop when done.</span>
                  </div>
                )}

                {askLoading && !askRecording && (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="knowledge-ask-thinking">
                    <div className="relative w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white">
                      <Sparkles className="w-4 h-4" />
                      <span className="absolute inset-0 rounded-lg bg-amber-400/40 animate-ping" />
                    </div>
                    <Soundwave active color="bg-amber-500" />
                    <span className="text-sm font-medium text-amber-700">MASTER MIND is searching the team's knowledge…</span>
                  </div>
                )}

                {askResult && !askLoading && (
                  <div className="space-y-4" data-testid="knowledge-ask-result">
                    <div className="p-5 rounded-xl bg-gradient-to-br from-amber-50 via-orange-50 to-white border border-amber-200">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-amber-600">
                          <Sparkles className="w-3.5 h-3.5" /> Answer
                        </div>
                        {askResult.answer && (
                          <button
                            type="button"
                            onClick={() => ttsLoading ? null : (askAudioRef.current ? stopSpeaking() : speakAnswer(askResult.answer))}
                            disabled={ttsLoading}
                            className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-100 hover:bg-amber-200 active:bg-amber-300 rounded-full px-2.5 py-1 text-xs font-semibold disabled:opacity-50"
                            title={askAudioRef.current ? 'Stop playback' : 'Play answer aloud'}
                            data-testid="knowledge-replay-tts"
                          >
                            {ttsLoading
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Volume2 className="w-3.5 h-3.5" />}
                            <span>{askAudioRef.current ? 'Stop' : 'Play'}</span>
                          </button>
                        )}
                      </div>
                      <p className="text-base text-slate-800 whitespace-pre-wrap leading-relaxed">
                        {askResult.answer}
                      </p>
                    </div>

                    {(askResult.citations || []).length > 0 && (
                      <div>
                        <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1">
                          <BookOpen className="w-3 h-3" /> Sources
                        </p>
                        <ScrollArea className="max-h-[35vh] pr-2">
                          <div className="space-y-2">
                            {askResult.citations.map((c) => (
                              <div
                                key={c.entry_id}
                                className="p-3 rounded-lg border bg-white hover:border-amber-300 transition-colors"
                                data-testid={`knowledge-citation-${c.ref}`}
                              >
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">[{c.ref}]</Badge>
                                  <span className="font-medium text-sm">{c.title || 'Untitled'}</span>
                                  <span className="ml-auto text-[11px] text-slate-500">
                                    by <strong>{c.submitter_name}</strong> · {(c.created_at || '').slice(0, 10)}
                                  </span>
                                </div>
                                {c.snippet && (
                                  <p className="text-xs text-slate-600 mt-1.5 line-clamp-2">{c.snippet}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    )}
                  </div>
                )}

                {!askResult && !askLoading && !askRecording && (
                  <div className="text-center text-slate-400 text-sm py-12">
                    <NerdFace className="w-24 h-24 mx-auto mb-3" />
                    Ask in any language — even Urdu by voice — and I'll find the answers your teammates have shared.
                  </div>
                )}
              </TabsContent>

              {/* ===== TELL ME ===== */}
              <TabsContent value="tell" className="space-y-4">
                <textarea
                  value={tellText}
                  onChange={(e) => setTellText(e.target.value)}
                  placeholder="Share knowledge with the team — write here in English, Urdu, Arabic, anything. We'll auto-translate."
                  className="w-full min-h-[100px] p-3 rounded-lg border border-slate-200 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200 text-sm bg-white"
                  data-testid="knowledge-tell-text"
                />

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => tellRecording ? stopRecording('tell') : startRecording('tell')}
                    className={tellRecording ? 'border-orange-500 text-orange-600' : ''}
                    data-testid="knowledge-tell-mic"
                  >
                    {tellRecording ? <Square className="w-4 h-4 mr-1.5" /> : <Mic className="w-4 h-4 mr-1.5" />}
                    {tellRecording ? 'Stop recording' : 'Record voice'}
                  </Button>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      accept=".pdf,.xlsx,.xls,.csv,.txt,.md,image/*"
                      onChange={(e) => setTellFiles([...tellFiles, ...Array.from(e.target.files || [])])}
                      data-testid="knowledge-tell-file"
                    />
                    <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm bg-white hover:bg-slate-50">
                      <Paperclip className="w-4 h-4" /> Attach files
                    </span>
                  </label>
                  <span className="text-[11px] text-slate-500 flex items-center gap-1">
                    <Languages className="w-3 h-3" /> PDF · XLSX · Images supported
                  </span>
                  <Button
                    onClick={submitTell}
                    disabled={tellSubmitting}
                    className="ml-auto bg-orange-600 hover:bg-orange-700"
                    data-testid="knowledge-tell-submit"
                  >
                    {tellSubmitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                    Submit to MASTER MIND
                  </Button>
                </div>

                {tellRecording && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
                    <Soundwave active color="bg-orange-500" />
                    <span className="text-sm font-medium text-orange-700">Recording… speak in any language.</span>
                  </div>
                )}

                {tellRecordedBlob && !tellRecording && (
                  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-orange-50/60 border border-orange-200">
                    <CheckCircle2 className="w-4 h-4 text-orange-600" />
                    <span className="text-sm text-orange-700">Voice note ready ({Math.round(tellRecordedBlob.size / 1024)} KB)</span>
                    <button
                      type="button"
                      onClick={() => setTellRecordedBlob(null)}
                      className="ml-auto text-orange-600 hover:bg-orange-100 rounded p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {tellFiles.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {tellFiles.map((f, i) => (
                      <Badge key={i} variant="outline" className="gap-1 pr-1 bg-white" data-testid={`knowledge-tell-file-chip-${i}`}>
                        {fileIcon(f.type, f.name)}
                        <span className="truncate max-w-[160px]">{f.name}</span>
                        <button onClick={() => removeFile(i)} className="hover:bg-slate-100 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                      Latest entries
                      {recentEntries.length > 0 && (
                        <span className="ml-2 px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold tracking-normal" data-testid="knowledge-entries-count">
                          {recentEntries.length}
                        </span>
                      )}
                    </p>
                    <Input
                      value={recentSearch}
                      onChange={(ev) => setRecentSearch(ev.target.value)}
                      placeholder="Search notes…"
                      className="h-7 max-w-[180px] text-xs"
                      data-testid="knowledge-recent-search"
                    />
                  </div>
                  {recentEntries.length === 0 ? (
                    <div className="text-sm text-slate-400 py-6 text-center">
                      {recentSearch.trim() ? 'No matches — try a different search.' : 'No entries yet — be the first to TELL.'}
                    </div>
                  ) : (
                    <ScrollArea className="h-[420px] pr-2">
                      <div className="space-y-1.5">
                        {recentEntries.map((e) => {
                          const isEditing = editingEntryId === e.id;
                          const mine = canModify(e);
                          return (
                            <div key={e.id} className="p-2.5 rounded-lg border bg-white" data-testid={`knowledge-recent-${e.id}`}>
                              {isEditing ? (
                                <div className="space-y-2">
                                  <Input
                                    value={editDraftTitle}
                                    onChange={(ev) => setEditDraftTitle(ev.target.value)}
                                    placeholder="Title (optional)"
                                    className="h-8 text-sm"
                                    data-testid={`knowledge-edit-title-${e.id}`}
                                  />
                                  <textarea
                                    value={editDraftText}
                                    onChange={(ev) => setEditDraftText(ev.target.value)}
                                    className="w-full min-h-[80px] p-2 rounded-md border text-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200"
                                    data-testid={`knowledge-edit-text-${e.id}`}
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button size="sm" variant="outline" onClick={cancelEdit} disabled={editSaving}>Cancel</Button>
                                    <Button
                                      size="sm"
                                      onClick={() => saveEdit(e.id)}
                                      disabled={editSaving}
                                      className="bg-orange-600 hover:bg-orange-700"
                                      data-testid={`knowledge-edit-save-${e.id}`}
                                    >
                                      {editSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                                      Save
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm">{e.title}</span>
                                    <Badge variant="outline" className="text-[10px]">
                                      {e.source_kind === 'voice' ? '🎙 Voice' : e.source_kind === 'file' ? '📎 File' : '📝 Text'}
                                    </Badge>
                                    <span className="ml-auto text-[11px] text-slate-500">
                                      {e.submitter_name} · {(e.created_at || '').slice(0, 10)}
                                    </span>
                                    {mine && (
                                      <div className="flex items-center gap-0.5">
                                        <button
                                          type="button"
                                          onClick={() => startEdit(e)}
                                          className="p-1 rounded hover:bg-slate-100 text-slate-500"
                                          data-testid={`knowledge-edit-btn-${e.id}`}
                                          title="Edit"
                                        >
                                          <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => deleteEntry(e.id)}
                                          className="p-1 rounded hover:bg-orange-100 text-orange-600"
                                          data-testid={`knowledge-delete-btn-${e.id}`}
                                          title="Delete"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-600 mt-1 line-clamp-2">{e.english_text}</p>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default KnowledgeBubble;
